import { useState, useCallback, ReactElement } from 'react'
import * as Yup from 'yup'

import { MdCheck } from 'react-icons/md'
import Button from 'shared/components/Button'
import Modal from 'shared/components/Modal'
import Text from 'shared/components/Text'
import Input from 'shared/components/Input'
import { colors } from 'shared/styles/colors'
import iconInfo from 'shared/assets/icons/icon-info.png'
import SnackBar from 'shared/components/SnackBar'
import { ISignIn } from 'modules/auth/domain/usecases/SignInImpl'
import { useAuth } from 'modules/auth/hooks/auth'

import {
  Container,
  ContainerIcon,
  StyledForm,
  ContainerContent,
  FormWrapper,
  Icon,
} from './styles'

interface IModalSenhaProps {
  openModalPassword: boolean
  setOpenModalPassword(state: boolean): void
  signIn: ISignIn
  handleExecuteCallback(): void
  handleCloseModal?(): void
}

interface IFormProps {
  password: string
}

const ModalSenha = ({
  openModalPassword,
  setOpenModalPassword,
  signIn,
  handleExecuteCallback,
  handleCloseModal,
}: IModalSenhaProps): ReactElement => {
  const [loading, setLoading] = useState(false)
  const [password] = useState('')
  const { user } = useAuth()
  const [openSnack, setOpenSnack] = useState(false)
  const [message, setMessage] = useState('')

  const handleOnCloseModal = useCallback(() => {
    setOpenModalPassword(false)

    handleCloseModal && handleCloseModal()
  }, [handleCloseModal, setOpenModalPassword])

  const handleOkButton = useCallback(
    async (values: IFormProps) => {
      setLoading(true)

      try {
        await signIn.execute({
          username: user,
          password: values.password,
        })

        setLoading(false)
        setOpenModalPassword(false)

        handleExecuteCallback()
      } catch (e: unknown) {
        setOpenSnack(true)
        setLoading(false)
        if (e instanceof Error) {
          if (e.message === 'Network Error') {
            setMessage('Serviço indisponível no momento.')
            return
          }
          if (e.message === 'Invalid user credentials') {
            setMessage('Credenciais inválidas')
            return
          }
        }
        setMessage('Erro ao validar senha')
      }
    },
    [setOpenModalPassword, signIn, handleExecuteCallback, user],
  )

  return (
    <>
      <Modal
        openModal={openModalPassword}
        size="small"
        showCloseButton={true}
        onCloseModal={handleOnCloseModal}
      >
        <Container>
          <ContainerIcon>
            <Icon src={iconInfo} />
          </ContainerIcon>

          <ContainerContent>
            <Text size={24} weight="700">
              Digite sua senha para confirmar essa ação
            </Text>

            <StyledForm
              enableReinitialize
              onSubmit={values => handleOkButton(values as IFormProps)}
              initialValues={{
                password,
              }}
              validationSchema={() => ({
                password: Yup.string().min(
                  6,
                  'A senha deve conter no mínimo 6 caracteres',
                ),
              })}
            >
              {formControl => (
                <FormWrapper>
                  <Input
                    placeholder="******"
                    name="password"
                    type="password"
                    formControl={formControl}
                  />

                  <Button
                    color={colors.green}
                    type="submit"
                    onClick={formControl.handleSubmit}
                    disabled={loading}
                    loading={loading}
                  >
                    <MdCheck />
                    Confirmar
                  </Button>
                </FormWrapper>
              )}
            </StyledForm>
          </ContainerContent>
        </Container>
      </Modal>
      <SnackBar
        open={openSnack}
        message={message}
        setOpen={setOpenSnack}
        severity={'error'}
      />
    </>
  )
}

export default ModalSenha
